import { sanitizeSingleLineText } from "./textSecurity";

const DEFAULT_GOUV_ENDPOINT = "https://api-adresse.data.gouv.fr/search/";
const DEFAULT_NOMINATIM_ENDPOINT = "https://nominatim.openstreetmap.org/search";
const MIN_QUERY_LENGTH = 3;
const searchCache = new Map();

function getCustomEndpoint() {
  return String(process.env.REACT_APP_FORWARD_GEOCODE_URL || "").trim();
}

function normalizeText(value) {
  if (typeof value !== "string" && typeof value !== "number") {
    return "";
  }

  return sanitizeSingleLineText(String(value || ""));
}

function normalizeCoordinates(lng, lat) {
  if (lng === null || lng === undefined || lat === null || lat === undefined) {
    return null;
  }

  if (String(lng).trim() === "" || String(lat).trim() === "") {
    return null;
  }

  const safeLng = Number(lng);
  const safeLat = Number(lat);

  if (!Number.isFinite(safeLng) || !Number.isFinite(safeLat)) {
    return null;
  }
  if (Math.abs(safeLng) > 180 || Math.abs(safeLat) > 90) return null;

  return [safeLng, safeLat];
}

// ── Normalisation format api-adresse.data.gouv.fr (GeoJSON) ──────────────────

function normalizeSuggestionGouv(feature) {
  if (!feature?.geometry?.coordinates) return { resolved: false };

  const [lng, lat] = feature.geometry.coordinates;
  const coordinates = normalizeCoordinates(lng, lat);
  const p = feature.properties || {};

  const name  = normalizeText(p.name  || p.label || "");
  const city  = normalizeText(p.city  || "");
  const postcode = normalizeText(p.postcode || "");
  const cityWithPostcode = postcode && city ? `${city} ${postcode}` : city;

  let label;
  if (name && cityWithPostcode) label = `${name}, ${cityWithPostcode}`;
  else if (name)                label = name;
  else                          label = normalizeText(p.label || "");

  return {
    coordinates,
    label,
    name,
    address: name,
    city:    cityWithPostcode,
    country: "France",
    resolved: Boolean(coordinates),
  };
}

async function fetchGouvResults(query, limit) {
  const params = new URLSearchParams({
    q:            query,
    limit:        String(limit),
    autocomplete: "1",
  });

  const response = await fetch(
    `${DEFAULT_GOUV_ENDPOINT}?${params.toString()}`,
    { headers: { Accept: "application/json" } }
  );

  if (!response.ok) {
    throw new Error(`Geocode gouv error: ${response.status}`);
  }

  const data = await response.json();
  return Array.isArray(data.features) ? data.features : [];
}

// ── Nominatim (OpenStreetMap) — POI : bars, restaurants, lieux ───────────────

async function fetchNominatimResults(query, limit) {
  const params = new URLSearchParams({
    q:              query,
    format:         "jsonv2",
    addressdetails: "1",
    limit:          String(limit),
  });

  const response = await fetch(
    `${DEFAULT_NOMINATIM_ENDPOINT}?${params.toString()}`,
    { headers: { Accept: "application/json" } }
  );

  if (!response.ok) {
    throw new Error(`Nominatim error: ${response.status}`);
  }

  const data = await response.json();
  return Array.isArray(data) ? data : [];
}

// ── Normalisation format proxy custom (Nominatim-like) ────────────────────────

function extractAddressLine(addressObj = {}) {
  const parts = [
    addressObj.house_number,
    addressObj.road || addressObj.pedestrian || addressObj.footway || addressObj.path,
  ]
    .map((v) => normalizeText(v))
    .filter(Boolean);

  return parts.join(" ") || normalizeText(addressObj.neighbourhood || addressObj.suburb || "");
}

function normalizeSuggestionProxy(payload = {}) {
  const coordinates = normalizeCoordinates(
    payload?.lon ?? payload?.longitude,
    payload?.lat ?? payload?.latitude
  );
  const addressObj =
    payload?.address && Object.getPrototypeOf(payload.address) === Object.prototype
      ? payload.address
      : {};

  const name    = normalizeText(payload?.name ?? addressObj.amenity ?? "");
  const address = extractAddressLine(addressObj);
  const city    = normalizeText(
    addressObj.city ?? addressObj.town ?? addressObj.village ?? addressObj.municipality ?? ""
  );
  const country = normalizeText(addressObj.country ?? "");

  let label;
  if (name && city)    label = `${name}, ${city}`;
  else if (name)       label = name;
  else if (address && city) label = `${address}, ${city}`;
  else                 label = normalizeText(payload?.display_name ?? "");

  return { coordinates, label, name, address, city, country, resolved: Boolean(coordinates) };
}

async function fetchProxyResults(endpoint, query, limit, countryCode) {
  const response = await fetch(endpoint, {
    method:  "POST",
    headers: { "Content-Type": "application/json" },
    body:    JSON.stringify({ query, limit, countrycodes: countryCode || undefined }),
  });

  if (!response.ok) {
    throw new Error(`Geocode proxy error: ${response.status}`);
  }

  return response.json();
}

// ── Déduplication ─────────────────────────────────────────────────────────────

function deduplicateByProximity(suggestions, toleranceDeg = 0.0008) {
  const kept = [];

  for (const s of suggestions) {
    const isDupe = kept.some(
      (k) =>
        k.name.toLowerCase() === s.name.toLowerCase() &&
        k.address.toLowerCase() === s.address.toLowerCase() &&
        Math.abs(k.coordinates[0] - s.coordinates[0]) < toleranceDeg &&
        Math.abs(k.coordinates[1] - s.coordinates[1]) < toleranceDeg
    );

    if (!isDupe) kept.push(s);
  }

  return kept;
}

// ── Export principal ──────────────────────────────────────────────────────────

export async function geocodeSearch(query, { limit = 5, countryCode = "", provider = "default" } = {}) {
  const normalizedQuery = normalizeText(query);

  if (!normalizedQuery || normalizedQuery.length < MIN_QUERY_LENGTH) {
    return [];
  }

  // Essai explicite : la production ne choisit pas Photon par défaut.
  if (provider === "photon") {
    const safeLimit = Math.min(10, Math.max(1, Number(limit) || 5));
    const cacheKey = `photon:${normalizedQuery.toLowerCase()}:${countryCode}:${safeLimit}`;
    if (searchCache.has(cacheKey)) return searchCache.get(cacheKey);
    const params = new URLSearchParams({ q: normalizedQuery, lang: "fr", limit: String(safeLimit) });
    if (/^[a-z]{2}$/i.test(countryCode)) params.set("countrycode", countryCode.toUpperCase());
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 6000);
    try {
      const response = await fetch(`https://photon.komoot.io/api/?${params}`, { headers: { Accept: "application/json" }, signal: controller.signal });
      if (!response.ok) throw new Error(`Photon error: ${response.status}`);
      const payload = await response.json();
      const results = (Array.isArray(payload.features) ? payload.features : []).map((feature) => {
        const p = feature.properties || {};
        const coords = feature.geometry?.coordinates;
        const coordinates = normalizeCoordinates(coords?.[0], coords?.[1]);
        const address = [normalizeText(p.housenumber), normalizeText(p.street)].filter(Boolean).join(" ");
        const city = normalizeText(p.city || p.town || p.village || p.locality || "");
        const name = normalizeText(p.name || address || city);
        return { coordinates, name, address, city, country: normalizeText(p.country), label: [name, city].filter(Boolean).join(", "), resolved: Boolean(coordinates), placeId: p.osm_type && p.osm_id ? `osm:${p.osm_type}:${p.osm_id}` : null };
      }).filter((item) => item.resolved && item.name);
      const unique = deduplicateByProximity(results).slice(0, safeLimit);
      searchCache.set(cacheKey, unique);
      return unique;
    } finally { clearTimeout(timer); }
  }

  const customEndpoint = getCustomEndpoint();

  // Proxy custom (si REACT_APP_FORWARD_GEOCODE_URL est défini)
  if (customEndpoint) {
    const payload = await fetchProxyResults(customEndpoint, normalizedQuery, limit, countryCode);
    const results = (Array.isArray(payload) ? payload : [payload])
      .map(normalizeSuggestionProxy)
      .filter((r) => r.resolved);

    return deduplicateByProximity(results).slice(0, limit);
  }

  // Cache pour éviter les doubles requêtes à la frappe
  const cacheKey = `${normalizedQuery.toLowerCase()}:${countryCode}:${limit}`;
  if (searchCache.has(cacheKey)) return searchCache.get(cacheKey);

  // Gouv.fr (adresses postales précises) + Nominatim (POI : bars, restaurants, lieux) en parallèle
  const [gouvSettled, nominatimSettled] = await Promise.allSettled([
    fetchGouvResults(normalizedQuery, limit),
    fetchNominatimResults(normalizedQuery, limit),
  ]);

  if (gouvSettled.status === "rejected" && nominatimSettled.status === "rejected") {
    throw gouvSettled.reason || nominatimSettled.reason;
  }

  const gouvFeatures = gouvSettled.status === "fulfilled" ? gouvSettled.value : [];
  const nominatimItems = nominatimSettled.status === "fulfilled" ? nominatimSettled.value : [];

  const gouvResults      = gouvFeatures.map(normalizeSuggestionGouv).filter((r) => r.resolved);
  const nominatimResults = nominatimItems.map(normalizeSuggestionProxy).filter((r) => r.resolved);

  // Une adresse numérotée privilégie les adresses ; une recherche par nom
  // donne d'abord leur place aux établissements, sans les masquer derrière cinq rues.
  const isNumberedAddress = /^\d+\s/.test(normalizedQuery);
  const merged = isNumberedAddress ? [...gouvResults, ...nominatimResults] : [...nominatimResults, ...gouvResults];
  const results = deduplicateByProximity(merged).slice(0, limit);

  searchCache.set(cacheKey, results);
  return results;
}
